import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { MultiSelect } from 'primereact/multiselect';
import { RadioButton } from 'primereact/radiobutton';
import { Messages } from 'primereact/messages';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import { Dialog } from 'primereact/dialog';
import Container from 'react-bootstrap/Container';
import adminApi from '../api/adminApi';
import adminCatalogApi from '../api/adminCatalogApi';
import resourcesApi from '../api/resourcesApi';
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';

const TARGET_MODE = {
  ALL: 'all',
  ONLY: 'only',
  ALL_EXCEPT: 'allExcept',
};

const isHttpUrl = (value) => {
  if (!value) return true;
  try {
    const url = new URL(String(value));
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

const getApiErrorMessage = (error, fallbackMessage) => {
  if (error && error.response && typeof error.response.data === 'string' && error.response.data.trim()) {
    return error.response.data;
  }

  if (error && error.response && error.response.data && typeof error.response.data === 'object') {
    if (error.response.data.message) return error.response.data.message;
    if (error.response.data.title) return error.response.data.title;
    if (error.response.data.errors) {
      try {
        return JSON.stringify(error.response.data.errors);
      } catch (_) {
        // ignore
      }
    }
  }

  return (error && error.message) || fallbackMessage;
};

const uploadFileToPresignedUrl = async ({ uploadUrl, method, headers }, file) => {
  const safeMethod = String(method || 'PUT').toUpperCase();
  const extraHeaders = headers && typeof headers === 'object' ? headers : {};

  const requestHeaders = { ...extraHeaders };
  if (!Object.prototype.hasOwnProperty.call(requestHeaders, 'Content-Type')) {
    requestHeaders['Content-Type'] = file.type || 'application/octet-stream';
  }

  const response = await fetch(String(uploadUrl), {
    method: safeMethod,
    headers: requestHeaders,
    body: file,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Upload failed (${response.status})`);
  }
};

const AdminResourceEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEdit = Boolean(id);

  const msgs = useRef(null);
  const toast = useRef(null);
  const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect({
    deniedRedirectTo: '/admin/unauthorized',
    deniedActionLabel: 'Go To Admin Unauthorized',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [rolesOptions, setRolesOptions] = useState([]);
  const [customersOptions, setCustomersOptions] = useState([]);
  const [appProvidersOptions, setAppProvidersOptions] = useState([]);
  const [lmsOptions, setLmsOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    resourceType: '',
    iconType: '',
    category: '',
    portalScope: '',
    productScope: '',
    sortOrder: null,
    isActive: true,

    appProviderMode: TARGET_MODE.ALL,
    appProviderIds: [],

    customerMode: TARGET_MODE.ALL,
    customerIds: [],
    excludedCustomerIds: [],

    lmsMode: TARGET_MODE.ALL,
    lmsValues: [],

    roles: [],

    publicUrl: '',
    cloudFrontUrl: '',
    s3Bucket: '',
    s3Key: '',
  });

  const [selectedFile, setSelectedFile] = useState(null);

  const [supportPreviewVisible, setSupportPreviewVisible] = useState(false);
  const [supportPreviewLoading, setSupportPreviewLoading] = useState(false);
  const [supportPreviewCustomerId, setSupportPreviewCustomerId] = useState(null);
  const [supportPreviewAppProviderId, setSupportPreviewAppProviderId] = useState(null);
  const [supportPreviewLms, setSupportPreviewLms] = useState('');
  const [supportPreviewResults, setSupportPreviewResults] = useState([]);

  const [internalPreviewVisible, setInternalPreviewVisible] = useState(false);
  const [internalPreviewLoading, setInternalPreviewLoading] = useState(false);
  const [internalPreviewResults, setInternalPreviewResults] = useState([]);

  const [confirmLeaveVisible, setConfirmLeaveVisible] = useState(false);

  const allAppProviderIds = useMemo(
    () => (Array.isArray(appProvidersOptions) ? appProvidersOptions.map((x) => x.value) : []).filter((x) => Number.isFinite(Number(x))),
    [appProvidersOptions]
  );

  const allLmsValues = useMemo(
    () => (Array.isArray(lmsOptions) ? lmsOptions.map((x) => x.value) : []).filter(Boolean),
    [lmsOptions]
  );

  const portalScopeOptions = [
    { label: 'Support', value: 'support' },
    { label: 'Internal', value: 'internal' },
    { label: 'Both', value: 'both' },
  ];

  const productScopeOptions = [
    { label: 'MyFit', value: 'myfit' },
    { label: 'SmartTile', value: 'smarttile' },
    { label: 'Both', value: 'both' },
  ];

  const resourceTypeOptions = [
    { label: 'PDF', value: 'pdf' },
    { label: 'ZIP', value: 'zip' },
    { label: 'Link', value: 'link' },
    { label: 'Video', value: 'video' },
    { label: 'Guide', value: 'guide' },
    { label: 'Other', value: 'other' },
  ];

  const iconTypeOptions = [
    { label: 'PDF', value: 'pdf' },
    { label: 'Link', value: 'link' },
  ];

  const targetingModeOptions = [
    { label: 'All', value: TARGET_MODE.ALL },
    { label: 'Only selected', value: TARGET_MODE.ONLY },
    { label: 'All except selected', value: TARGET_MODE.ALL_EXCEPT },
  ];

  const showError = (error, fallbackMessage) => {
    if (handleAccessError(error)) return;

    const detail = getApiErrorMessage(error, fallbackMessage);

    if (msgs.current) {
      msgs.current.show({ severity: 'error', summary: '', detail, sticky: true });
    }
  };

  const normalizeLmsKey = (value) => String(value || '').trim();

  const loadLookups = async () => {
    const [roles, customers, appProviders, lmsTypes] = await Promise.all([
      adminApi.getRoles(),
      adminApi.getCustomersLookup(),
      adminApi.getAppProvidersLookup(),
      adminCatalogApi.getLmsTypes(),
    ]);

    setRolesOptions(Array.isArray(roles) ? roles.map((x) => ({ label: String(x), value: String(x) })) : []);
    setCustomersOptions(Array.isArray(customers) ? customers.map((x) => ({ label: x.name, value: x.id })) : []);
    setAppProvidersOptions(Array.isArray(appProviders) ? appProviders.map((x) => ({ label: x.name, value: x.id })) : []);

    const options = (Array.isArray(lmsTypes) ? lmsTypes : [])
      .map((item) => ({ label: String(item && item.name || '').trim(), value: normalizeLmsKey(item && item.lmsValue) }))
      .filter((item) => item.label && item.value)
      .sort((left, right) => left.label.localeCompare(right.label));
    setLmsOptions(options);
  };

  const normalizeCategoryValue = (value) => String(value || '').trim();

  const ensureCategoryOption = (value) => {
    const normalized = normalizeCategoryValue(value);
    if (!normalized) return;

    setCategoryOptions((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.some((x) => String(x && x.value) === normalized)) return current;

      const next = [...current, { label: normalized, value: normalized }];
      next.sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
      return next;
    });
  };

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);

      const pageSize = 200;
      const maxPages = 25;

      const values = new Set();
      let page = 1;
      let total = 0;

      while (page <= maxPages) {
        const result = await resourcesApi.getAdminResources({
          includeDeleted: true,
          page,
          pageSize,
        });

        const items = result && Array.isArray(result.items) ? result.items : [];
        total = result && result.total !== undefined && result.total !== null ? Number(result.total) : total;

        items.forEach((r) => {
          const category = normalizeCategoryValue(r && r.category);
          if (category) values.add(category);
        });

        if (items.length < pageSize) break;
        if (total && page * pageSize >= total) break;

        page += 1;
      }

      const list = Array.from(values);
      list.sort((a, b) => String(a).localeCompare(String(b)));
      setCategoryOptions(list.map((x) => ({ label: x, value: x })));

      // Ensure current form value is selectable even if not returned in the first pages.
      ensureCategoryOption(form.category);
    } catch (error) {
      // Categories are optional UI sugar; don't block editor unless this is an access issue.
      if (handleAccessError(error)) return;
    } finally {
      setCategoriesLoading(false);
    }
  };

  const hydrateFromResource = (resource) => {
    const allowedAppProviderIds = Array.isArray(resource.allowedAppProviderIds) ? resource.allowedAppProviderIds : [];
    const allowedCustomerIds = Array.isArray(resource.allowedCustomerIds) ? resource.allowedCustomerIds : [];
    const excludedCustomerIds = Array.isArray(resource.excludedCustomerIds) ? resource.excludedCustomerIds : [];
    const allowedLmsValues = Array.isArray(resource.allowedLmsValues) ? resource.allowedLmsValues : [];

    const roles = Array.isArray(resource.roles)
      ? resource.roles
      : Array.isArray(resource.allowedRoles)
        ? resource.allowedRoles
        : [];

    ensureCategoryOption(resource && resource.category);

    setForm((prev) => ({
      ...prev,
      title: String(resource.title || ''),
      description: String(resource.description || ''),
      resourceType: String(resource.resourceType || ''),
      iconType: String(resource.iconType || ''),
      category: String(resource.category || ''),
      portalScope: String(resource.portalScope || ''),
      productScope: String(resource.productScope || ''),
      sortOrder: resource.sortOrder === 0 || Number.isFinite(Number(resource.sortOrder)) ? Number(resource.sortOrder) : null,
      isActive: resource.isActive === undefined ? true : Boolean(resource.isActive),

      appProviderMode: allowedAppProviderIds.length === 0 ? TARGET_MODE.ALL : TARGET_MODE.ONLY,
      appProviderIds: allowedAppProviderIds.map((x) => Number(x)).filter((x) => Number.isFinite(x)),

      customerMode: excludedCustomerIds.length > 0 ? TARGET_MODE.ALL_EXCEPT : allowedCustomerIds.length > 0 ? TARGET_MODE.ONLY : TARGET_MODE.ALL,
      customerIds: allowedCustomerIds.map((x) => Number(x)).filter((x) => Number.isFinite(x)),
      excludedCustomerIds: excludedCustomerIds.map((x) => Number(x)).filter((x) => Number.isFinite(x)),

      lmsMode: allowedLmsValues.length === 0 ? TARGET_MODE.ALL : TARGET_MODE.ONLY,
      lmsValues: allowedLmsValues.map((x) => String(x || '')).filter(Boolean),

      roles: roles.map((x) => String(x || '')).filter(Boolean),

      publicUrl: String(resource.publicUrl || ''),
      cloudFrontUrl: String(resource.cloudFrontUrl || ''),
      s3Bucket: String(resource.s3Bucket || ''),
      s3Key: String(resource.s3Key || ''),
    }));
  };

  const load = async () => {
    try {
      setLoading(true);
      clearAccessIssue();
      if (msgs.current) msgs.current.clear();

      await loadLookups();
      await loadCategories();

      if (isEdit) {
        const resource = await resourcesApi.getResourceById(id);
        hydrateFromResource(resource || {});
      }
    } catch (error) {
      showError(error, 'Failed to load resource.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const buildAllowedAppProviderIds = () => {
    if (form.appProviderMode === TARGET_MODE.ALL) return [];

    const selected = (form.appProviderIds || []).map((x) => Number(x)).filter((x) => Number.isFinite(x));

    if (form.appProviderMode === TARGET_MODE.ONLY) return selected;

    // allExcept: compute allowed list from known app providers
    if (!Array.isArray(allAppProviderIds) || allAppProviderIds.length === 0) {
      throw new Error('Cannot compute app provider targeting without loading app providers list.');
    }

    const excluded = new Set(selected);
    return allAppProviderIds.filter((x) => !excluded.has(x));
  };

  const buildAllowedLmsValues = () => {
    if (form.lmsMode === TARGET_MODE.ALL) return [];

    const selected = (form.lmsValues || []).map((x) => String(x || '').trim()).filter(Boolean);

    if (form.lmsMode === TARGET_MODE.ONLY) return selected;

    if (!Array.isArray(allLmsValues) || allLmsValues.length === 0) {
      throw new Error('Cannot compute LMS targeting without loading LMS list.');
    }

    const excluded = new Set(selected);
    return allLmsValues.filter((x) => !excluded.has(x));
  };

  const buildCustomerTargeting = () => {
    const selected = (form.customerIds || []).map((x) => Number(x)).filter((x) => Number.isFinite(x));
    const excluded = (form.excludedCustomerIds || []).map((x) => Number(x)).filter((x) => Number.isFinite(x));

    if (form.customerMode === TARGET_MODE.ALL) {
      return { allowedCustomerIds: [], excludedCustomerIds: [] };
    }

    if (form.customerMode === TARGET_MODE.ONLY) {
      return { allowedCustomerIds: selected, excludedCustomerIds: [] };
    }

    return { allowedCustomerIds: [], excludedCustomerIds: excluded };
  };

  const validate = () => {
    const errors = [];

    const title = String(form.title || '').trim();
    if (!title) errors.push('Title is required.');

    const required = (key, label) => {
      if (!String(form[key] || '').trim()) errors.push(`${label} is required.`);
    };

    required('resourceType', 'Resource type');
    required('iconType', 'Icon type');
    required('category', 'Category');
    required('portalScope', 'Portal scope');
    required('productScope', 'Product scope');

    const hasUrl = Boolean(String(form.publicUrl || '').trim() || String(form.cloudFrontUrl || '').trim());
    const hasS3 = Boolean(String(form.s3Bucket || '').trim() && String(form.s3Key || '').trim());

    if (!hasUrl && !hasS3) {
      errors.push('Provide either a URL (publicUrl/cloudFrontUrl) or an uploaded file (s3Bucket + s3Key).');
    }

    if (form.publicUrl && !isHttpUrl(form.publicUrl)) {
      errors.push('publicUrl must be a valid http(s) URL.');
    }

    if (form.cloudFrontUrl && !isHttpUrl(form.cloudFrontUrl)) {
      errors.push('cloudFrontUrl must be a valid http(s) URL.');
    }

    if (form.lmsMode !== TARGET_MODE.ALL && (!Array.isArray(allLmsValues) || allLmsValues.length === 0)) {
      errors.push('LMS targeting is unavailable because no LMS types were returned by the server.');
    }

    return errors;
  };

  const save = async () => {
    try {
      if (msgs.current) msgs.current.clear();

      const errors = validate();
      if (errors.length > 0) {
        if (msgs.current) {
          errors.forEach((detail) => msgs.current.show({ severity: 'warn', summary: '', detail }));
        }
        return;
      }

      setSaving(true);

      const allowedAppProviderIds = buildAllowedAppProviderIds();
      const allowedLmsValues = buildAllowedLmsValues();
      const { allowedCustomerIds, excludedCustomerIds } = buildCustomerTargeting();

      const payload = {
        title: String(form.title || '').trim(),
        description: String(form.description || '').trim() || null,
        resourceType: String(form.resourceType || '').trim(),
        iconType: String(form.iconType || '').trim(),
        category: String(form.category || '').trim(),
        portalScope: String(form.portalScope || '').trim(),
        productScope: String(form.productScope || '').trim(),
        sortOrder: Number.isFinite(Number(form.sortOrder)) ? Number(form.sortOrder) : null,
        isActive: Boolean(form.isActive),

        allowedAppProviderIds,
        allowedCustomerIds,
        excludedCustomerIds,
        allowedLmsValues,

        roles: Array.isArray(form.roles) ? form.roles.map((x) => String(x || '').trim()).filter(Boolean) : [],
        allowedRoles: Array.isArray(form.roles) ? form.roles.map((x) => String(x || '').trim()).filter(Boolean) : [],

        publicUrl: String(form.publicUrl || '').trim() || null,
        cloudFrontUrl: String(form.cloudFrontUrl || '').trim() || null,
        s3Bucket: String(form.s3Bucket || '').trim() || null,
        s3Key: String(form.s3Key || '').trim() || null,
      };

      // Remove null keys to avoid backend validation confusion.
      Object.keys(payload).forEach((key) => {
        if (payload[key] === null) delete payload[key];
      });

      if (isEdit) {
        await resourcesApi.updateResource(id, payload);
      } else {
        await resourcesApi.createResource(payload);
      }

      if (toast.current) {
        toast.current.show({ severity: 'success', summary: '', detail: isEdit ? 'Resource updated.' : 'Resource created.' });
      }

      navigate(`/admin/resources${location.search || ''}`);
    } catch (error) {
      showError(error, 'Failed to save resource.');
    } finally {
      setSaving(false);
    }
  };

  const startUpload = async () => {
    if (!selectedFile) {
      if (msgs.current) {
        msgs.current.show({ severity: 'warn', summary: '', detail: 'Select a file to upload.' });
      }
      return;
    }

    if (!String(form.portalScope || '').trim() || !String(form.productScope || '').trim()) {
      if (msgs.current) {
        msgs.current.show({ severity: 'warn', summary: '', detail: 'Select portalScope and productScope before uploading.' });
      }
      return;
    }

    try {
      setUploading(true);
      if (msgs.current) msgs.current.clear();

      const uploadInfo = await resourcesApi.getUploadUrl({
        fileName: selectedFile.name,
        contentType: selectedFile.type || 'application/octet-stream',
        portalScope: form.portalScope,
        productScope: form.productScope,
      });

      if (!uploadInfo || !uploadInfo.uploadUrl) {
        throw new Error('Upload URL not returned by server.');
      }

      await uploadFileToPresignedUrl(uploadInfo, selectedFile);

      setForm((prev) => ({
        ...prev,
        s3Bucket: String(uploadInfo.s3Bucket || prev.s3Bucket || ''),
        s3Key: String(uploadInfo.s3Key || prev.s3Key || ''),
        // If backend returns URLs, populate for convenience.
        publicUrl: prev.publicUrl || String(uploadInfo.publicUrl || ''),
        cloudFrontUrl: prev.cloudFrontUrl || String(uploadInfo.cloudFrontUrl || ''),
      }));

      if (toast.current) {
        toast.current.show({ severity: 'success', summary: '', detail: 'File uploaded.' });
      }
    } catch (error) {
      showError(error, 'Failed to upload file.');
    } finally {
      setUploading(false);
    }
  };

  const loadSupportPreview = async () => {
    try {
      setSupportPreviewLoading(true);
      const results = await resourcesApi.previewSupportResources({
        customerId: supportPreviewCustomerId,
        appProviderId: supportPreviewAppProviderId,
        lms: supportPreviewLms,
      });
      setSupportPreviewResults(Array.isArray(results) ? results : []);
    } catch (error) {
      showError(error, 'Failed to load support preview.');
    } finally {
      setSupportPreviewLoading(false);
    }
  };

  const loadInternalPreview = async () => {
    try {
      setInternalPreviewLoading(true);
      const results = await resourcesApi.previewInternalResources();
      setInternalPreviewResults(Array.isArray(results) ? results : []);
    } catch (error) {
      showError(error, 'Failed to load internal preview.');
    } finally {
      setInternalPreviewLoading(false);
    }
  };

  const progressbar = (
    <div className="card mt-3">
      <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>
  );

  const back = () => {
    if (saving || uploading) {
      setConfirmLeaveVisible(true);
      return;
    }

    navigate(`/admin/resources${location.search || ''}`);
  };

  if (accessIssue) {
    return <AccessState {...accessIssue} />;
  }

  return (
    <Card className="justify-content-md-center mt-5">
      <Container fluid="md">
        <div className="panel">
          <div className="flex align-content-center flex-wrap">
            <Button link icon="pi pi-arrow-left" onClick={back} className="flex mb-3" />
            <h4 className="flex mt-2 mb-3 ml-3 mr-3">{isEdit ? 'Admin - Edit Resource' : 'Admin - Create Resource'}</h4>
          </div>
        </div>

        <Messages ref={msgs} />
        <Toast ref={toast} />
        {loading && progressbar}

        {!loading && (
          <div className="card p-3">
            <div className="grid">
              <div className="col-12 md:col-6">
                <label className="block mb-2">Title *</label>
                <InputText value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} style={{ width: '100%' }} />
              </div>

              <div className="col-12 md:col-6">
                <label className="block mb-2">Category *</label>
                <Dropdown
                  value={form.category}
                  options={categoryOptions}
                  editable
                  filter
                  placeholder={categoriesLoading ? 'Loading categories…' : 'Select or type a category'}
                  onChange={(e) => {
                    const raw =
                      e && e.value !== undefined && e.value !== null
                        ? e.value
                        : e && e.target && e.target.value !== undefined && e.target.value !== null
                          ? e.target.value
                          : '';

                    const next = String(raw || '');
                    setForm((p) => ({ ...p, category: next }));
                    ensureCategoryOption(next);
                  }}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="col-12">
                <label className="block mb-2">Description</label>
                <InputTextarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={3} style={{ width: '100%' }} />
              </div>

              <div className="col-12 md:col-4">
                <label className="block mb-2">Resource Type *</label>
                <Dropdown value={form.resourceType} options={resourceTypeOptions} onChange={(e) => setForm((p) => ({ ...p, resourceType: e.value }))} placeholder="Select" style={{ width: '100%' }} />
              </div>

              <div className="col-12 md:col-4">
                <label className="block mb-2">Icon Type *</label>
                <Dropdown value={form.iconType} options={iconTypeOptions} onChange={(e) => setForm((p) => ({ ...p, iconType: e.value }))} placeholder="Select" style={{ width: '100%' }} />
              </div>

              <div className="col-12 md:col-4">
                <label className="block mb-2">Sort Order</label>
                <InputNumber value={form.sortOrder} onValueChange={(e) => setForm((p) => ({ ...p, sortOrder: e.value }))} style={{ width: '100%' }} />
              </div>

              <div className="col-12 md:col-6">
                <label className="block mb-2">Portal Scope *</label>
                <Dropdown value={form.portalScope} options={portalScopeOptions} onChange={(e) => setForm((p) => ({ ...p, portalScope: e.value }))} placeholder="Select" style={{ width: '100%' }} />
              </div>

              <div className="col-12 md:col-6">
                <label className="block mb-2">Product Scope *</label>
                <Dropdown value={form.productScope} options={productScopeOptions} onChange={(e) => setForm((p) => ({ ...p, productScope: e.value }))} placeholder="Select" style={{ width: '100%' }} />
              </div>

              <div className="col-12 md:col-6">
                <div className="flex align-items-center gap-2 mt-3">
                  <span>Active</span>
                  <InputSwitch checked={Boolean(form.isActive)} onChange={(e) => setForm((p) => ({ ...p, isActive: Boolean(e.value) }))} />
                </div>
              </div>

              <div className="col-12">
                <h5 className="mt-4 mb-2">Targeting</h5>
              </div>

              <div className="col-12 md:col-6">
                <label className="block mb-2">App Providers</label>
                <div className="flex flex-column gap-2">
                  {targetingModeOptions.map((opt) => (
                    <div key={opt.value} className="flex align-items-center gap-2">
                      <RadioButton
                        inputId={`ap-${opt.value}`}
                        name="appProviderMode"
                        value={opt.value}
                        checked={form.appProviderMode === opt.value}
                        onChange={(e) => setForm((p) => ({ ...p, appProviderMode: e.value, appProviderIds: [] }))}
                      />
                      <label htmlFor={`ap-${opt.value}`}>{opt.label}</label>
                    </div>
                  ))}
                </div>
                {form.appProviderMode !== TARGET_MODE.ALL && (
                  <MultiSelect
                    value={form.appProviderIds}
                    options={appProvidersOptions}
                    onChange={(e) => setForm((p) => ({ ...p, appProviderIds: e.value }))}
                    placeholder={form.appProviderMode === TARGET_MODE.ONLY ? 'Select app providers' : 'Select app providers to exclude'}
                    className="mt-2"
                    display="chip"
                    style={{ width: '100%' }}
                  />
                )}
              </div>

              <div className="col-12 md:col-6">
                <label className="block mb-2">Customers</label>
                <div className="flex flex-column gap-2">
                  {targetingModeOptions.map((opt) => (
                    <div key={opt.value} className="flex align-items-center gap-2">
                      <RadioButton
                        inputId={`cust-${opt.value}`}
                        name="customerMode"
                        value={opt.value}
                        checked={form.customerMode === opt.value}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            customerMode: e.value,
                            customerIds: [],
                            excludedCustomerIds: [],
                          }))
                        }
                      />
                      <label htmlFor={`cust-${opt.value}`}>{opt.label}</label>
                    </div>
                  ))}
                </div>

                {form.customerMode === TARGET_MODE.ONLY && (
                  <MultiSelect
                    value={form.customerIds}
                    options={customersOptions}
                    onChange={(e) => setForm((p) => ({ ...p, customerIds: e.value }))}
                    placeholder="Select customers"
                    className="mt-2"
                    display="chip"
                    style={{ width: '100%' }}
                  />
                )}

                {form.customerMode === TARGET_MODE.ALL_EXCEPT && (
                  <MultiSelect
                    value={form.excludedCustomerIds}
                    options={customersOptions}
                    onChange={(e) => setForm((p) => ({ ...p, excludedCustomerIds: e.value }))}
                    placeholder="Select customers to exclude"
                    className="mt-2"
                    display="chip"
                    style={{ width: '100%' }}
                  />
                )}
              </div>

              <div className="col-12 md:col-6">
                <label className="block mb-2">LMS</label>
                <div className="flex flex-column gap-2">
                  {targetingModeOptions.map((opt) => (
                    <div key={opt.value} className="flex align-items-center gap-2">
                      <RadioButton
                        inputId={`lms-${opt.value}`}
                        name="lmsMode"
                        value={opt.value}
                        checked={form.lmsMode === opt.value}
                        onChange={(e) => setForm((p) => ({ ...p, lmsMode: e.value, lmsValues: [] }))}
                      />
                      <label htmlFor={`lms-${opt.value}`}>{opt.label}</label>
                    </div>
                  ))}
                </div>

                {form.lmsMode !== TARGET_MODE.ALL && (
                  <MultiSelect
                    value={form.lmsValues}
                    options={lmsOptions}
                    onChange={(e) => setForm((p) => ({ ...p, lmsValues: e.value }))}
                    placeholder={form.lmsMode === TARGET_MODE.ONLY ? 'Select LMS types' : 'Select LMS types to exclude'}
                    className="mt-2"
                    display="chip"
                    style={{ width: '100%' }}
                  />
                )}
              </div>

              <div className="col-12 md:col-6">
                <label className="block mb-2">Roles (optional)</label>
                <MultiSelect
                  value={form.roles}
                  options={rolesOptions}
                  onChange={(e) => setForm((p) => ({ ...p, roles: e.value }))}
                  placeholder="Select roles"
                  display="chip"
                  style={{ width: '100%' }}
                />
              </div>

              <div className="col-12">
                <h5 className="mt-4 mb-2">File / URL</h5>
              </div>

              <div className="col-12 md:col-6">
                <label className="block mb-2">publicUrl</label>
                <InputText
                  value={form.publicUrl}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      publicUrl: e.target.value,
                      ...(e.target.value ? { s3Bucket: '', s3Key: '' } : {}),
                    }))
                  }
                  placeholder="https://..."
                  style={{ width: '100%' }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="block mb-2">cloudFrontUrl</label>
                <InputText
                  value={form.cloudFrontUrl}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      cloudFrontUrl: e.target.value,
                      ...(e.target.value ? { s3Bucket: '', s3Key: '' } : {}),
                    }))
                  }
                  placeholder="https://..."
                  style={{ width: '100%' }}
                />
              </div>

              <div className="col-12">
                <div className="card p-3 mt-2">
                  <div className="flex align-items-center justify-content-between flex-wrap gap-3">
                    <div>
                      <strong>Upload file</strong>
                      <div className="text-muted">Upload to S3 using a pre-signed URL.</div>
                      {form.s3Bucket && form.s3Key && (
                        <div className="mt-2">
                          <div>
                            <strong>s3Bucket:</strong> {form.s3Bucket}
                          </div>
                          <div>
                            <strong>s3Key:</strong> {form.s3Key}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex align-items-center gap-2">
                      <input
                        type="file"
                        onChange={(e) => {
                          const file = e.target && e.target.files ? e.target.files[0] : null;
                          setSelectedFile(file || null);
                        }}
                      />
                      <Button
                        label={uploading ? 'Uploading...' : 'Upload'}
                        icon="pi pi-upload"
                        onClick={startUpload}
                        disabled={uploading || saving}
                      />
                      <Button
                        label="Clear"
                        className="p-button-text"
                        onClick={() => {
                          setSelectedFile(null);
                          setForm((p) => ({ ...p, s3Bucket: '', s3Key: '' }));
                        }}
                        disabled={uploading || saving}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="flex justify-content-end gap-2 mt-3">
                  <Button label="Cancel" className="p-button-text" onClick={back} disabled={saving || uploading} />
                  <Button label={saving ? 'Saving...' : 'Save'} icon="pi pi-save" onClick={save} disabled={saving || uploading} />
                </div>
              </div>

              <div className="col-12">
                <h5 className="mt-4 mb-2">Preview</h5>
                <div className="flex flex-wrap gap-2">
                  <Button
                    label="Preview as Support"
                    icon="pi pi-eye"
                    className="p-button-text"
                    onClick={() => setSupportPreviewVisible(true)}
                  />
                  <Button
                    label="Preview Internal"
                    icon="pi pi-eye"
                    className="p-button-text"
                    onClick={() => setInternalPreviewVisible(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <Dialog
          header="Preview as Support"
          visible={supportPreviewVisible}
          onHide={() => setSupportPreviewVisible(false)}
          style={{ width: '900px' }}
          modal
        >
          <div className="grid">
            <div className="col-12 md:col-4">
              <label className="block mb-2">Customer</label>
              <Dropdown
                value={supportPreviewCustomerId}
                options={[{ label: 'Select', value: null }, ...customersOptions]}
                onChange={(e) => setSupportPreviewCustomerId(e.value)}
                placeholder="Select"
                style={{ width: '100%' }}
              />
            </div>
            <div className="col-12 md:col-4">
              <label className="block mb-2">App Provider</label>
              <Dropdown
                value={supportPreviewAppProviderId}
                options={[{ label: 'Select', value: null }, ...appProvidersOptions]}
                onChange={(e) => setSupportPreviewAppProviderId(e.value)}
                placeholder="Select"
                style={{ width: '100%' }}
              />
            </div>
            <div className="col-12 md:col-4">
              <label className="block mb-2">LMS</label>
              <Dropdown
                value={supportPreviewLms}
                options={[{ label: 'Select', value: '' }, ...lmsOptions]}
                onChange={(e) => setSupportPreviewLms(e.value)}
                placeholder="Select"
                style={{ width: '100%' }}
              />
            </div>
            <div className="col-12">
              <div className="flex justify-content-end">
                <Button label={supportPreviewLoading ? 'Loading...' : 'Load Preview'} onClick={loadSupportPreview} disabled={supportPreviewLoading} />
              </div>
            </div>
            <div className="col-12">
              {supportPreviewLoading && progressbar}
              {!supportPreviewLoading && (
                <ul>
                  {supportPreviewResults.map((r) => (
                    <li key={r.id || r.title}>{String(r.title || r.name || r.id)}</li>
                  ))}
                  {supportPreviewResults.length === 0 && <li>No results.</li>}
                </ul>
              )}
            </div>
          </div>
        </Dialog>

        <Dialog
          header="Preview Internal"
          visible={internalPreviewVisible}
          onHide={() => setInternalPreviewVisible(false)}
          style={{ width: '900px' }}
          modal
        >
          <div className="flex justify-content-end mb-2">
            <Button label={internalPreviewLoading ? 'Loading...' : 'Load Preview'} onClick={loadInternalPreview} disabled={internalPreviewLoading} />
          </div>
          {internalPreviewLoading && progressbar}
          {!internalPreviewLoading && (
            <ul>
              {internalPreviewResults.map((r) => (
                <li key={r.id || r.title}>{String(r.title || r.name || r.id)}</li>
              ))}
              {internalPreviewResults.length === 0 && <li>No results.</li>}
            </ul>
          )}
        </Dialog>

        <Dialog
          header="Leave page?"
          visible={confirmLeaveVisible}
          onHide={() => setConfirmLeaveVisible(false)}
          modal
          style={{ width: '520px' }}
        >
          <p>An action is in progress. Leave anyway?</p>
          <div className="flex justify-content-end gap-2">
            <Button label="Stay" className="p-button-text" onClick={() => setConfirmLeaveVisible(false)} />
            <Button
              label="Leave"
              onClick={() => {
                setConfirmLeaveVisible(false);
                navigate(`/admin/resources${location.search || ''}`);
              }}
            />
          </div>
        </Dialog>
      </Container>
    </Card>
  );
};

export default AdminResourceEditor;
